// frontend/app/components/admin/LessonMetadataFields.js
//
// The lesson's own fields — everything above the steps.
//
// Built from the authoring wizard's primitives rather than the admin panel's
// inputs: this is the same content the wizard writes, and an admin who has
// seen one lesson form should not have to learn a second one.
//
// Slug, board and topic are shown but not editable. The slug is what the
// seeder matches a file to, and what a saved link points at — renaming it here
// would quietly orphan the lesson from the file it came from.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import {
  ChipSelect,
  Field,
  SectionCard,
  StringListField,
  TextField,
} from "../lessonPlans/authoring/WizardFields";
import { LESSON_STATUSES } from "./lessonAdminDraft";

// The three levels the seeded lessons use. Accents come from the palette
// passed in rather than from literals of their own — the admin panel has one
// set of colours and this screen is not the place to start a second.
const difficultyOptions = (colors) => [
  { value: "Foundation", label: "Foundation", accent: colors.success },
  { value: "Core", label: "Core", accent: colors.info },
  { value: "Extended", label: "Extended", accent: colors.warning },
];

const statusAccents = (colors) => ({
  draft: colors.warning,
  published: colors.success,
  archived: colors.textMuted,
});

/**
 * What the save preserves without showing it.
 *
 * `materials` and `resource_shelf` are offered nowhere on this form: the
 * toolkit shelf and the workflow columns were the only things that rendered
 * them, and both came out of the lesson overview. An editable field whose
 * contents nobody will ever read is worse than no field — an admin would write
 * copy into a void with no way to find that out.
 *
 * They are still worth one line, because the save sends the whole document and
 * an admin who cannot see them here would reasonably fear the save dropped them.
 */
const CarriedThroughNote = ({ colors, draft }) => {
  const carried = [];
  if (draft.concepts?.length)
    carried.push(`${draft.concepts.length} concept${draft.concepts.length === 1 ? "" : "s"}`);
  if (draft.materials?.length)
    carried.push(`${draft.materials.length} material${draft.materials.length === 1 ? "" : "s"}`);
  if (Object.keys(draft.resource_shelf || {}).length) carried.push("a resource shelf");
  if (!carried.length) return null;

  return (
    <Text style={[styles.carriedNote, { color: colors.textMuted }]}>
      This lesson also holds {carried.join(", ")}, saved unchanged and not editable here.
      Concepts are what blocks below can be pointed at; materials and the resource shelf
      are not shown on any screen at the moment.
    </Text>
  );
};

/** Keeps a stored value that is not one of the presets selectable and visible. */
const withCurrentValue = (options, value) => {
  const current = String(value || "").trim();
  if (!current || options.some((option) => option.value === current)) return options;
  return [...options, { value: current, label: current }];
};

const LessonMetadataFields = ({ colors, draft, onChange }) => {
  const set = (fields) => onChange({ ...draft, ...fields });
  const statusAccent = statusAccents(colors);

  return (
    <>
      <SectionCard
        colors={colors}
        title="Lesson"
        subtitle={`${draft.slug || "no slug"} · ${String(draft.base_board || "").toUpperCase()}${
          draft.topic_name ? ` · ${draft.topic_name}` : ""
        }`}
        icon="book-open-variant"
      >
        <Field colors={colors} label="Title" required>
          <TextField
            colors={colors}
            value={draft.title}
            onChange={(title) => set({ title })}
            placeholder="Lesson title"
            accessibilityLabel="Lesson title"
          />
        </Field>

        <Field colors={colors} label="Subtitle">
          <TextField
            colors={colors}
            value={draft.subtitle}
            onChange={(subtitle) => set({ subtitle })}
            placeholder="Cambridge IGCSE Biology (0610) 9.1.1"
            accessibilityLabel="Lesson subtitle"
          />
        </Field>

        <Field
          colors={colors}
          label="Description"
          hint="Shown on the lesson card, and used to match a teacher's description of what they want to teach."
        >
          <TextField
            colors={colors}
            value={draft.description}
            onChange={(description) => set({ description })}
            placeholder="What this lesson covers, in a sentence or two."
            accessibilityLabel="Lesson description"
            multiline
          />
        </Field>

        <View style={styles.row}>
          <View style={styles.rowItem}>
            <Field colors={colors} label="Lesson number">
              <TextField
                colors={colors}
                value={draft.lesson_no}
                onChange={(lesson_no) => set({ lesson_no })}
                placeholder="1"
                keyboardType="numeric"
                accessibilityLabel="Lesson number"
              />
            </Field>
          </View>
          <View style={styles.rowItem}>
            <Field colors={colors} label="Duration (minutes)">
              <TextField
                colors={colors}
                value={draft.duration_minutes}
                onChange={(duration_minutes) => set({ duration_minutes })}
                placeholder="40"
                keyboardType="numeric"
                accessibilityLabel="Duration in minutes"
              />
            </Field>
          </View>
        </View>

        <Field colors={colors} label="Difficulty">
          <ChipSelect
            colors={colors}
            options={withCurrentValue(difficultyOptions(colors), draft.difficulty)}
            value={draft.difficulty}
            onChange={(difficulty) => set({ difficulty })}
            accessibilityLabel="Difficulty"
          />
        </Field>

        <Field
          colors={colors}
          label="Status"
          hint="Only published lessons are offered to teachers."
        >
          <ChipSelect
            colors={colors}
            options={LESSON_STATUSES.map((status) => ({
              value: status,
              label: status[0].toUpperCase() + status.slice(1),
              accent: statusAccent[status],
            }))}
            value={draft.status}
            onChange={(status) => set({ status })}
            accessibilityLabel="Status"
          />
        </Field>
      </SectionCard>

      <SectionCard
        colors={colors}
        title="Objectives"
        subtitle="What a student can do by the end. Shown on the lesson overview."
        icon="target"
      >
        <StringListField
          colors={colors}
          items={draft.objectives}
          onChange={(objectives) => set({ objectives })}
          placeholder="Objective"
          addLabel="Add objective"
          ordered
        />
      </SectionCard>

      <SectionCard
        colors={colors}
        title="Prerequisites"
        subtitle="What a class needs to have met already."
        icon="stairs-up"
      >
        <StringListField
          colors={colors}
          items={draft.prerequisites}
          onChange={(prerequisites) => set({ prerequisites })}
          placeholder="Prerequisite"
          addLabel="Add prerequisite"
        />
      </SectionCard>

      <CarriedThroughNote colors={colors} draft={draft} />
    </>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: "row", gap: 12 },
  rowItem: { flex: 1, minWidth: 110 },
  carriedNote: { fontSize: 11, lineHeight: 17, marginBottom: 12 },
});

export default LessonMetadataFields;
